/**
 * Text Compare Service
 * Implements text comparison and diff operations
 * Provides line-by-line comparison with change detection
 */

import { EncodingOperation } from '../types';
import { ProcessingError, ValidationError } from '../utils/errorHandler';
import { CommonValidator } from '../validation/commonValidation';

export type ChangeType = 'added' | 'removed' | 'modified' | 'unchanged';

export interface DiffLine {
  lineNumber: number;
  content: string;
  type: ChangeType;
}

export interface DiffResult {
  leftLines: DiffLine[];
  rightLines: DiffLine[];
  addedCount: number;
  removedCount: number;
  modifiedCount: number;
  unchangedCount: number;
}

export class TextCompareService {
  /**
   * Compare two texts
   */
  static async compare(leftText: string, rightText: string): Promise<EncodingOperation> {
    try {
      // Validate inputs
      const leftValidation = CommonValidator.validateText(leftText,
        CommonValidator.getValidationRules('text-compare' as any)
      );
      const rightValidation = CommonValidator.validateText(rightText,
        CommonValidator.getValidationRules('text-compare' as any)
      );

      if (!leftValidation.isValid) {
        throw new ValidationError(leftValidation.error!, 'text-compare' as any, leftValidation.suggestion);
      }
      if (!rightValidation.isValid) {
        throw new ValidationError(rightValidation.error!, 'text-compare' as any, rightValidation.suggestion);
      }

      // Perform comparison
      const diff = this.performDiff(leftText, rightText);

      return {
        id: this.generateOperationId(),
        toolType: 'text-compare' as any,
        operationType: 'compare',
        inputData: `${leftText}\n---SEPARATOR---\n${rightText}`,
        outputData: diff,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          leftLength: leftText.length,
          rightLength: rightText.length,
          addedCount: diff.addedCount,
          removedCount: diff.removedCount,
          modifiedCount: diff.modifiedCount,
          unchangedCount: diff.unchangedCount
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to compare texts',
        'text-compare' as any,
        'Please check your input and try again'
      );
    }
  }

  /**
   * Perform text diff
   */
  private static performDiff(leftText: string, rightText: string): DiffResult {
    // Handle empty strings - split('') returns [''] which is wrong
    const leftLines = leftText === '' ? [] : leftText.split('\n');
    const rightLines = rightText === '' ? [] : rightText.split('\n');

    const leftDiffLines: DiffLine[] = [];
    const rightDiffLines: DiffLine[] = [];

    let addedCount = 0;
    let removedCount = 0;
    let modifiedCount = 0;
    let unchangedCount = 0;

    const maxLines = Math.max(leftLines.length, rightLines.length);

    for (let i = 0; i < maxLines; i++) {
      const leftLine = leftLines[i];
      const rightLine = rightLines[i];

      if (leftLine === undefined) {
        // Line only exists in right (added)
        rightDiffLines.push({
          lineNumber: i + 1,
          content: rightLine,
          type: 'added'
        });
        leftDiffLines.push({
          lineNumber: i + 1,
          content: '',
          type: 'added'
        });
        addedCount++;
      } else if (rightLine === undefined) {
        // Line only exists in left (removed)
        leftDiffLines.push({
          lineNumber: i + 1,
          content: leftLine,
          type: 'removed'
        });
        rightDiffLines.push({
          lineNumber: i + 1,
          content: '',
          type: 'removed'
        });
        removedCount++;
      } else if (leftLine === rightLine) {
        // Lines are identical (unchanged)
        leftDiffLines.push({
          lineNumber: i + 1,
          content: leftLine,
          type: 'unchanged'
        });
        rightDiffLines.push({
          lineNumber: i + 1,
          content: rightLine,
          type: 'unchanged'
        });
        unchangedCount++;
      } else {
        // Lines are different (modified)
        leftDiffLines.push({
          lineNumber: i + 1,
          content: leftLine,
          type: 'modified'
        });
        rightDiffLines.push({
          lineNumber: i + 1,
          content: rightLine,
          type: 'modified'
        });
        modifiedCount++;
      }
    }

    return {
      leftLines: leftDiffLines,
      rightLines: rightDiffLines,
      addedCount,
      removedCount,
      modifiedCount,
      unchangedCount
    };
  }

  /**
   * Format diff result for display
   */
  static formatDiff(diff: DiffResult): string {
    const lines: string[] = [];

    lines.push('=== Comparison Summary ===');
    lines.push(`Added: ${diff.addedCount} lines`);
    lines.push(`Removed: ${diff.removedCount} lines`);
    lines.push(`Modified: ${diff.modifiedCount} lines`);
    lines.push(`Unchanged: ${diff.unchangedCount} lines`);
    lines.push('');

    lines.push('=== Changes ===');
    for (let i = 0; i < diff.leftLines.length; i++) {
      const leftLine = diff.leftLines[i];
      const rightLine = diff.rightLines[i];

      if (leftLine.type !== 'unchanged') {
        const prefix = leftLine.type === 'removed' ? '- ' :
                      leftLine.type === 'added' ? '+ ' : '~ ';
        lines.push(`Line ${leftLine.lineNumber}: ${prefix}${leftLine.content || rightLine.content}`);
      }
    }

    return lines.join('\n');
  }

  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `text-compare-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}
